import { tableName } from '../../components/env';

// Reduce a value to digits only. The patients_with_ehrid table stores phone
// numbers as bare digit strings (e.g. "9705751191"), so we strip the wizard's
// already-normalized tel_num (which may be formatted like "(626) 555-5558").
export function bareDigits(value) {
  if (value == null) return '';
  return String(value).replace(/\D/g, '');
}

function ehrIdOf(row) {
  const v = row.EHR_ID;
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

// patients_with_ehrid: one row per patient, JSON arrays for phones/visits.
// ehr_id is the text PRIMARY KEY sourced from the file's EHR/MRN column, so a
// row with no EHR ID cannot be inserted — toRecord returns null and the caller
// skips it. Mirrors the backend build's ON CONFLICT (ehr_id) DO NOTHING via an
// upsert with ignoreDuplicates.
//
// The record shape is exactly the column set backend/api/patient_table.py's
// insert_with_ehrid writes: ehr_id, patient_name, email, phone_numbers,
// visit_dates, source_record_ids (created_at defaults on the DB). This table has
// no consent/dob columns, so we must not send them or PostgREST 400s.
const patientsWithEhrid = {
  key: 'patients_with_ehrid',
  label: 'Patient table (patients_with_ehrid)',
  table: 'patients_with_ehrid',
  onConflict: 'ehr_id',
  listColumns: ['phone_numbers', 'visit_dates'],
  requiresEhrId: true,
  dedup: { idColumn: 'ehr_id', idSelect: 'ehr_id' },
  // The mapping step lists this table's REAL columns live. `columnSpec` maps a
  // real column to the internal field key that carries its special parsing
  // (phone_numbers -> tel_num normalizes + arrays it, visit_dates -> date_
  // parses + arrays it, etc.). Columns absent here are mapped as plain text.
  columnSpec: {
    ehr_id: 'EHR_ID',
    patient_name: 'patient_name',
    email: 'email',
    phone_numbers: 'tel_num',
    visit_dates: 'date_',
  },
  // Auto-managed columns hidden from the mapping list (DB default / import sets).
  managedColumns: ['created_at', 'source_record_ids'],
  isEligible(row) {
    return row.EHR_ID != null && String(row.EHR_ID).trim() !== '';
  },
  toRecord(row, extraColumnNames = []) {
    const ehrId = ehrIdOf(row);
    if (!ehrId) return null;
    const phone = row.tel_num ? bareDigits(row.tel_num) : '';
    const rec = {
      ehr_id: ehrId,
      patient_name: row.patient_name || null,
      email: row.email || null,
      phone_numbers: phone ? [phone] : [],
      visit_dates: row.date_ ? [row.date_] : [],
      source_record_ids: [],
    };
    extraColumnNames.forEach((n) => {
      if (row[n] != null && row[n] !== '') rec[n] = row[n];
    });
    return rec;
  },
};

// notes-c: the original flat, one-row-per-visit target. Insert rows unchanged.
const notesC = {
  key: 'notes-c',
  label: 'Visit records (notes-c)',
  table: tableName,
  onConflict: null,
  listColumns: [],
  requiresEhrId: false,
  dedup: { idColumn: 'EHR_ID', idSelect: '"EHR_ID"' },
  // notes-c stores each field in a same-named column (identity toRecord), so a
  // real column maps to the same-named field key (EHR_ID keeps its casing). Any
  // column absent here (including ones the user added) maps as plain text.
  columnSpec: {
    patient_name: 'patient_name',
    email: 'email',
    tel_num: 'tel_num',
    home_phone: 'home_phone',
    dob: 'dob',
    type: 'type',
    EHR_ID: 'EHR_ID',
    physician_name: 'physician_name',
    date_: 'date_',
    time_: 'time_',
  },
  // Only the clearly DB/system-managed columns are hidden; everything else shows.
  managedColumns: ['id', 'created_at', 'updated_at', 'user_id'],
  isEligible() { return true; },
  toRecord(row) { return row; },
};

export const TARGET_TABLES = {
  patients_with_ehrid: patientsWithEhrid,
  'notes-c': notesC,
};

export const DEFAULT_TARGET = 'patients_with_ehrid';

export function getTarget(key) {
  return TARGET_TABLES[key] || TARGET_TABLES[DEFAULT_TARGET];
}
